import './claim-entity.detail.overview.scss';
import './common.scss';
import './claim-details.scss';

import { Box, Tab, Tabs } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import React, { SyntheticEvent, useState, useCallback, useEffect } from 'react';

import {
  Claim,
  useClaimInformationQuery,
  useUpsertClaimInformationMutation,
  useCloseClaimDetailsQuery,
} from '@converge/graphql';
import { ClaimDetailsTabPanel } from './components/claim-details-tab-panel';
import { ClaimEntityPageContainer } from './claim-entity.page.container';

import { ClaimOverviewPage } from './claim-overview';
import {
  CircularLoader,
  ErrorPageLayout,
  PaperLayout,
} from '../../../presentation';
import { useConvergeSnackBar } from '../../../contexts';
import { LossTypesCarousel } from './components/loss-types-carousel';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { ClaimDataPage } from './claim-data';
import { IProps } from './claim-entity.detail.overview';
import MitreAttack from './mitre-attack';

// Tab name mapping
const TAB_NAMES = ['overview', 'loss-types', 'data', 'mitre-attack'] as const;
type TabName = (typeof TAB_NAMES)[number];

// Helper function for tab accessibility attributes
function getTabProps(index: number) {
  return {
    id: `claim-details-tab-${index}`,
    'aria-controls': `claim-details-tabpanel-${index}`,
  };
}

// Helper function to get tab index from tab name
function getTabIndexFromName(tabName: string): number {
  const index = TAB_NAMES.indexOf(tabName as TabName);
  return index >= 0 ? index : 0;
}

// Helper function to get tab name from index
function getTabNameFromIndex(index: number): TabName {
  return TAB_NAMES[index] || TAB_NAMES[0];
}

export interface IClaimDetailsOutletProps {
  insuredUuid: string;
}

export interface IClaimDetailsProps {
  claimEntity: Claim;
  claimUuid: string;
}

export const ClaimDetails = () => {
  const { claimEntity, claimUuid, isNotActive } = useOutletContext<IProps>();

  if (!claimEntity) {
    return (
      <ErrorPageLayout
        message="No claim data found"
        messageId="insured.detail.page.claimEntityNotFound"
      />
    );
  }

  return (
    <ClaimDetailsInner
      data={claimEntity}
      claimUuid={claimUuid}
      isNotActive={isNotActive}
    />
  );
};

export const ClaimDetailsInner = ({
  data,
  claimUuid,
  isNotActive,
}: {
  data: Claim;
  claimUuid?: string;
  isNotActive: boolean;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize tab value from URL parameter
  const currentTab = searchParams.get('tab') || 'overview';
  const [tabValue, setTabValue] = useState(getTabIndexFromName(currentTab));

  // Update tab value when URL changes
  useEffect(() => {
    const urlTab = searchParams.get('tab') || 'overview';
    const newIndex = getTabIndexFromName(urlTab);
    setTabValue(newIndex);
  }, [searchParams]);

  const handleTabChange = (_event: SyntheticEvent, newValue: number) => {
    const tabName = getTabNameFromIndex(newValue);
    setTabValue(newValue);

    // Update URL with new tab parameter
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('tab', tabName);
    setSearchParams(newSearchParams);
  };

  const formMethods = useForm();

  // Fetch claim information data for loss types
  const {
    loading,
    error,
    data: claimInfoData,
  } = useClaimInformationQuery(claimUuid || '');

  // Mutation for saving loss types
  const { upsertClaimInformation } = useUpsertClaimInformationMutation();
  const { notify } = useConvergeSnackBar();

  // Hook for refetching close claim details
  const { refetch: refetchCloseClaimDetails } = useCloseClaimDetailsQuery({
    claimUuid: claimUuid || '',
  });

  // Auto-save function for loss types
  const handleLossTypesAutoSave = useCallback(
    async (updatedLossTypes: unknown) => {
      if (!claimUuid) {
        console.warn('No claimUuid provided for auto-save');
        return;
      }

      try {
        // Stringify the lossTypes payload
        // const stringifiedLossTypes = JSON.stringify(updatedLossTypes);
        notify('Saving...', {
          variant: 'info',
        });
        await upsertClaimInformation({
          claimInput: {
            claimUuid: claimUuid,
            lossTypes: updatedLossTypes,
          },
        });

        // Refetch close claim details after successful upsert
        await refetchCloseClaimDetails();

        notify('Loss types have been updated', {
          variant: 'success',
        });
      } catch (error) {
        console.error('Loss types have been failed:', error);
        notify('Loss types have been failed:', {
          variant: 'error',
        });
      }
    },
    [claimUuid, upsertClaimInformation, notify, refetchCloseClaimDetails]
  );

  return (
    <FormProvider {...formMethods}>
      <ClaimEntityPageContainer
        className="claim-details-container dairy-container"
        title="Claim Information"
      >
        <PaperLayout className="tab-container">
          <Box
            className="claim-details-container"
            sx={{
              position: 'relative',
              height: tabValue === 2 ? '62vh' : 'calc(100vh - 300px)',
              overflow: 'auto',
            }}
          >
            {/* Tab navigation - anchored */}
            <Box
              className="tab-header"
              sx={{
                position: 'fixed',
                top: '290px',
                zIndex: 2,
                width: '92.5%',
              }}
            >
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                aria-label="claim information tabs"
                sx={{
                  fontWeight: 'bold',
                  color: '#000000 !important',
                  borderBottom: '1px solid #000000',
                  width: '83.5% !important',
                  backgroundColor: 'white',
                  marginBottom: '10px',
                  marginLeft: '1.5rem',
                  '& .Mui-selected': {
                    fontWeight: 'bold',
                  },
                }}
                variant="fullWidth"
              >
                <Tab
                  sx={{
                    textTransform: 'none',
                    fontSize: '16px',
                    color: '#000000 !important',
                  }}
                  label="Overview"
                  {...getTabProps(0)}
                />
                <Tab
                  sx={{
                    textTransform: 'none',
                    fontSize: '16px',
                    color: '#000000 !important',
                  }}
                  label="Loss Types"
                  {...getTabProps(1)}
                />
                <Tab
                  sx={{
                    textTransform: 'none',
                    fontSize: '16px',
                    color: '#000000 !important',
                  }}
                  label="Data"
                  {...getTabProps(2)}
                />
                <Tab
                  sx={{
                    textTransform: 'none',
                    fontSize: '16px',
                    color: '#000000 !important',
                  }}
                  label="MITRE ATT&CK"
                  {...getTabProps(3)}
                />
              </Tabs>
            </Box>

            {/* Tab content using the ClaimDetailsTabPanel */}
            <Box sx={{ mt: 8 }}>
              <ClaimDetailsTabPanel value={tabValue} index={0}>
                <>
                  <ClaimOverviewPage
                    loading={loading}
                    dataInformation={claimInfoData}
                  />
                </>
              </ClaimDetailsTabPanel>

              <ClaimDetailsTabPanel value={tabValue} index={1}>
                <Box
                  sx={{
                    minHeight: '400px',
                  }}
                >
                  {loading && (
                    <CircularLoader className="circular-loader-style" />
                  )}
                  {error && (
                    <div>Error loading loss types: {error.message}</div>
                  )}
                  {!loading && !error && (
                    <LossTypesCarousel
                      lossTypes={(() => {
                        const rawLossTypes =
                          claimInfoData?.claims?.edges?.[0]?.node?.lossTypes;
                        if (typeof rawLossTypes === 'string') {
                          try {
                            return JSON.parse(rawLossTypes);
                          } catch (error) {
                            console.error(
                              'Failed to parse lossTypes JSON string:',
                              error
                            );
                            return null;
                          }
                        }
                        return rawLossTypes;
                      })()}
                      isNotActive={isNotActive}
                      claimUuid={claimUuid}
                      onSelectionChange={(selections) => {
                        console.log('Loss type selections:', selections);
                      }}
                      onAutoSave={handleLossTypesAutoSave}
                    />
                  )}
                </Box>
              </ClaimDetailsTabPanel>

              <ClaimDetailsTabPanel value={tabValue} index={2}>
                <>
                  {' '}
                  <ClaimDataPage />
                </>
              </ClaimDetailsTabPanel>

              <ClaimDetailsTabPanel value={tabValue} index={3}>
                <MitreAttack />
              </ClaimDetailsTabPanel>
            </Box>
          </Box>
        </PaperLayout>
      </ClaimEntityPageContainer>
    </FormProvider>
  );
};
